import { ReactNode, forwardRef, useId } from 'react';

import {
  FormGroup,
  FormGroupProps,
  FormHelpText,
  FormInput,
  FormInputIcon,
  FormInputIconProps,
  FormInputProps,
} from '@spotify-internal/encore-web';

export type FormTextFieldProps = Omit<FormInputProps, 'id' | 'error'> & {
  label: string;
  helperText?: ReactNode;
  error?: string | null;
  invalid?: boolean;
  iconLeading?: FormInputIconProps['iconLeading'];
  iconTrailing?: FormInputIconProps['iconTrailing'];
  indicator?: FormGroupProps['indicator'];
  popover?: FormGroupProps['popover'];
  'data-testid'?: string;
};

export const FormTextField = forwardRef<HTMLElement, FormTextFieldProps>(
  (
    {
      label,
      helperText,
      error,
      invalid = false,
      iconLeading,
      iconTrailing,
      indicator,
      popover,
      name,
      required,
      size = 'small',
      'data-testid': dataTestId = name,
      ...inputProps
    }: FormTextFieldProps,
    ref,
  ) => {
    const inputId = useId();
    const helpTextId = useId();
    const helpText = error ?? helperText;
    const hasError = invalid || Boolean(error);

    return (
      <FormGroup
        label={label}
        labelFor={inputId}
        indicator={indicator}
        popover={popover}
        data-testid={dataTestId}
      >
        <FormInputIcon iconLeading={iconLeading} iconTrailing={iconTrailing}>
          <FormInput
            data-track-source-anchor="0f5e36fda3;form-text-field-form-input" {...inputProps}
            ref={ref}
            id={inputId}
            name={name}
            size={size}
            error={hasError}
            required={required}
            aria-describedby={helpText ? helpTextId : undefined}
          />
        </FormInputIcon>
        {helpText && (
          <FormHelpText id={helpTextId} error={hasError}>
            {helpText}
          </FormHelpText>
        )}
      </FormGroup>
    );
  },
);

FormTextField.displayName = 'FormTextField';
